import type { FC } from 'react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEdit as ChangeIcon } from 'react-icons/fa';
import { Button, Stack } from '@mui/material';

import { LogicalOperator as LogicalOperatorEnum } from 'src/constants/dataline';
import Modal from 'src/components/common/Modal';
import { WorkspaceContext } from 'src/components/workspace/WorkspaceContext';

import { styles } from './index.styles';

export const LOGICAL_OPERATOR_TEST_ID = 'logical-operator';

export interface LogicalOperatorProps {
  changeOperator: (x: string, y: LogicalOperatorEnum, z: string | null) => void;
  comparisonRenderId: string;
  definitionlineRenderId?: string;
  isReadOnly?: boolean;
  operator?: LogicalOperatorEnum;
}

export const LogicalOperator: FC<LogicalOperatorProps> = ({
  changeOperator,
  comparisonRenderId,
  definitionlineRenderId = null,
  isReadOnly = false,
  operator = null,
}) => {
  const [showSelectOperatorBox, setShowSelectOperatorBox] = useState(false);
  const { t } = useTranslation('workspace');

  const toggleSelectOperatorBox = () => {
    setShowSelectOperatorBox((prev) => !prev);
  };

  const getOperatorString = useCallback(
    (value: LogicalOperatorEnum | null) => {
      let operatorString = t('or');
      operatorString =
        value === LogicalOperatorEnum.AND ? t('and') : operatorString;
      operatorString =
        value === LogicalOperatorEnum.EXCLUDE ? t('exclude') : operatorString;

      return operatorString;
    },
    [t],
  );

  return (
    <Stack
      direction="column"
      sx={styles.logicalOperator}
      data-test-id={LOGICAL_OPERATOR_TEST_ID}
    >
      <Button
        variant="cta"
        color="tertiary"
        sx={styles.button}
        disabled={isReadOnly}
        onClick={toggleSelectOperatorBox}
      >
        {getOperatorString(operator)}
      </Button>
      <Modal
        headline={t('changeOperator')}
        icon={<ChangeIcon size={23} />}
        isOpen={showSelectOperatorBox}
        onClose={toggleSelectOperatorBox}
      >
        <Stack direction="row" spacing={2} sx={{ width: '100%', pt: 5 }}>
          {Object.values(LogicalOperatorEnum).map((value) => (
            <Button
              variant="cta"
              fullWidth
              disabled={operator === value}
              key={value}
              onClick={() => {
                changeOperator(
                  comparisonRenderId,
                  value,
                  definitionlineRenderId,
                );
                toggleSelectOperatorBox();
              }}
            >
              {getOperatorString(value)}
            </Button>
          ))}
        </Stack>
      </Modal>
    </Stack>
  );
};

type LogicalOperatorWrapperProps = Omit<LogicalOperatorProps, 'changeOperator'>;

const LogicalOperatorWrapper: FC<LogicalOperatorWrapperProps> = (props) => (
  <WorkspaceContext.Consumer>
    {(context) =>
      context?.changeConnectingOperator ? (
        <LogicalOperator
          changeOperator={context.changeConnectingOperator}
          {...props}
        />
      ) : null
    }
  </WorkspaceContext.Consumer>
);

export default LogicalOperatorWrapper;
